import { Badge, Box, Button, Card, Flex, Text, VStack } from '@chakra-ui/react'
import type { LucideIcon } from 'lucide-react'

export interface InsightCardProps {
  /** Token used for the left accent border, e.g. `orange.solid`. */
  accent: string
  /** Chakra colorPalette applied to the top badge, e.g. `orange`. */
  colorPalette: string
  icon?: LucideIcon
  badgeLabel: string
  meta?: string
  title: string
  description: string
  tag?: string
  /** Action button label + handler. Omit both for a purely informational,
   * non-interactive card (no button, no pointer cursor, no click target). */
  actionLabel?: string
  onClick?: () => void
  /** When set (e.g. 'bob' | 'spin' | 'pulse' | 'drift' | 'flash'), the card's
   * `icon` is rendered as a large, accent-coloured decorative illustration in
   * the top-right corner, continuously animated (see `.usecase-anim--*` in
   * base.css) and emphasised on hover. Omit for the default small badge icon. */
  iconAnimation?: string
}

// Reusable "insight card" from the DayN prototype: a bordered card with a
// colored left accent, a top badge + optional meta, title/description, an
// optional tag chip, and an optional full-width bordered action button pinned
// to the bottom of the card. When no `onClick` is supplied the card is a
// static info card: no button, no pointer cursor, no click handler.
export function InsightCard({
  accent,
  colorPalette,
  icon: Icon,
  badgeLabel,
  meta,
  title,
  description,
  tag,
  actionLabel,
  onClick,
  iconAnimation,
}: InsightCardProps) {
  const interactive = Boolean(onClick)
  const showIllustration = Boolean(Icon && iconAnimation)
  return (
    <Card.Root
      bg="bg.panel"
      borderWidth="1px"
      borderColor="border"
      borderRadius="lg"
      borderLeftWidth="4px"
      borderLeftColor={accent}
      boxShadow="sm"
      h="100%"
      display="flex"
      flexDirection="column"
      position="relative"
      overflow={showIllustration ? 'hidden' : undefined}
      cursor={interactive ? 'pointer' : 'default'}
      onClick={interactive ? onClick : undefined}
      _hover={{
        borderColor: 'content.tertiary',
        boxShadow: 'md',
        '& .usecase-illus': { transform: 'scale(1.18)' },
      }}
      transition="border-color 0.2s, box-shadow 0.2s"
    >
      {showIllustration && Icon ? (
        <Box
          className="usecase-illus"
          position="absolute"
          top="16px"
          right="18px"
          color={accent}
          opacity={0.9}
          pointerEvents="none"
          transition="transform 0.25s ease"
        >
          <span className={`usecase-anim usecase-anim--${iconAnimation}`}>
            <Icon size={30} strokeWidth={1.75} />
          </span>
        </Box>
      ) : null}
      <Box px="22px" py="40px" flex={1} display="flex" flexDirection="column">
        <VStack align="stretch" gap={0} h="100%">
          <Flex justify="space-between" align="center" mb={3}>
            <Badge
              colorPalette={colorPalette}
              variant="subtle"
              borderRadius="lg"
              px={2.5}
              py={1}
              fontSize="xs"
              // Outdent by the chip's own left padding so the badge's leading
              // content lines up with the title/description below it.
              ms="-2.5"
            >
              {Icon && !showIllustration ? <Icon size={12} /> : null}
              {badgeLabel}
            </Badge>
            {meta ? (
              <Text fontSize="xs" color="content.tertiary">
                {meta}
              </Text>
            ) : null}
          </Flex>

          <Text
            fontSize="md"
            fontWeight="semibold"
            color="content.primary"
            lineHeight="1.3"
            mb={1.5}
          >
            {title}
          </Text>

          <Text fontSize="sm" color="content.secondary" lineHeight="1.5" mb={5}>
            {description}
          </Text>

          {tag ? (
            <Badge
              bg="bg.subtle"
              color="content.secondary"
              borderRadius="lg"
              px={2.5}
              py={0.5}
              fontSize="xs"
              fontWeight="semibold"
              mb={4}
              width="fit-content"
            >
              {tag}
            </Badge>
          ) : null}

          <Box flex={1} />

          {actionLabel && onClick ? (
            <Button
              variant="outline"
              w="100%"
              borderColor="border"
              fontWeight="medium"
              size="sm"
              onClick={(e) => {
                e.stopPropagation()
                onClick()
              }}
            >
              {actionLabel} →
            </Button>
          ) : null}
        </VStack>
      </Box>
    </Card.Root>
  )
}
